import type { Catalogue } from './core.js';
import type { MessageKey } from './da.js';

/**
 * English.
 *
 * Typed as `Catalogue<MessageKey>`, so this file **will not compile** while a
 * key exists in Danish and not here. That is the whole reason for hand-rolling
 * the translation layer: a missing message is a build failure rather than
 * Danish text quietly appearing on an English screen.
 *
 * ## What is deliberately not translated
 *
 * The bookkeeping vocabulary stays close to what a Danish accountant would
 * recognise where the English word is genuinely equivalent — "moms" is VAT,
 * "bogføring" is bookkeeping. Where it is not, the Danish word stays:
 *
 * - **Bilag** is translated as "documents", not "vouchers". A voucher means
 *   something else in English accounting software, and the thing here is
 *   literally the receipt or invoice that documents an entry.
 * - **CVR** stays CVR. It is the Danish company register number and has no
 *   English equivalent; calling it a "company number" would invite someone to
 *   type a different country's.
 * - **SAF-T**, **OIOUBL** and **Nemhandel** stay as they are — they are the
 *   names of the formats and the network.
 *
 * Account names, VAT code descriptions and everything else the API returns as
 * domain data are Danish in both locales, and no key here can change that.
 */
export const en: Catalogue<MessageKey> = {
  // ---------------------------------------------------------------------------
  // Shell
  // ---------------------------------------------------------------------------
  'app.name': 'Accounts',
  'shell.menu': 'Menu',
  'shell.mainNav': 'Main menu',
  'shell.account': 'Account',
  'shell.accountMenu': 'Your account',
  'shell.you': 'You',
  'shell.signOut': 'Sign out',
  'shell.signOutFailed': 'You are not signed out',

  'nav.overview': 'Overview',
  'nav.bookkeeping': 'Bookkeeping',
  'nav.attachments': 'Documents',
  'nav.inbound': 'Incoming mail',
  'nav.bank': 'Bank',
  'nav.invoices': 'Invoices',
  'nav.contacts': 'Contacts',
  'nav.vat': 'VAT',
  'nav.reports': 'Reports',
  'nav.settings': 'Settings',
  'nav.chartOfAccounts': 'Chart of accounts',
  'nav.saft': 'SAF-T',
  'nav.audit': 'Audit trail',
  'nav.mobileApp': 'Mobile app',
  'nav.companyDetails': '{name} details',
  'nav.companyDetailsFallback': 'Company details',
  'nav.myAccount': 'My account',
  'nav.mcp': 'MCP',

  // ---------------------------------------------------------------------------
  // Company switcher
  // ---------------------------------------------------------------------------
  'company.switch': 'Switch company',
  'company.choose': 'Choose company',
  'company.companies': 'Companies',
  'company.active': 'active',
  'company.switchFailed': 'Could not switch company',
  'company.add': 'Add company',
  'company.add.title': 'Add company',
  'company.add.name': 'Name',
  'company.add.cvr': 'CVR number',
  'company.add.cvrPlaceholder': '8 digits',
  'company.add.cvrHelp':
    'Can be filled in later. Required by the SAF-T export and by an electronic invoice.',
  'company.add.nameRequired': 'The company needs a name.',
  'company.add.cvrInvalid': 'A CVR number is exactly 8 digits.',
  'company.add.warningTitle': 'A company cannot be removed afterwards',
  'company.add.warning':
    'The audit trail cannot be deleted, and it points at the company. As soon as anything is written in the books, the company stays — even if it was a typo. Check the name.',
  'company.add.created': 'Company created',
  'company.add.createdDetail': '{name} — you are the owner.',
  'company.add.failed': 'The company was not created',

  // ---------------------------------------------------------------------------
  // Shared vocabulary
  // ---------------------------------------------------------------------------
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.create': 'Create',
  'common.tryAgain': 'Try again',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.hide': 'Hide',
  'common.never': 'never',
  'common.required': 'This field is required.',

  // ---------------------------------------------------------------------------
  // Errors and auth boundary
  // ---------------------------------------------------------------------------
  'error.generic': 'Something went wrong',
  'error.unreachable': 'Could not reach the server. Check that the API is running.',
  'error.reference': 'Reference:',
  'auth.sessionFailed': 'Could not load your session',
  'auth.noCompanies': 'No companies',
  'auth.noCompaniesDetail':
    'Your user is not attached to any company, so there are no books to show. Ask an owner to add you.',

  // ---------------------------------------------------------------------------
  // Login
  // ---------------------------------------------------------------------------
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.emailInvalid': 'Enter a valid email address.',
  'login.passwordMissing': 'The password is missing.',
  'login.totpPrompt': 'Enter the code from your authenticator app.',
  'login.totpLabel': 'One-time code',
  'login.totpSubmit': 'Confirm',
  'login.sessionNotKept':
    'Your email and password were accepted, but the browser did not keep the session. That happens when the page and the API are on different domains — open them on the same address.',

  // ---------------------------------------------------------------------------
  // Not found
  // ---------------------------------------------------------------------------
  'notFound.title': 'Page not found',
  'notFound.detail': 'That address does not match anything in the system.',
  'notFound.back': 'Back to overview',

  // ---------------------------------------------------------------------------
  // Dashboard
  // ---------------------------------------------------------------------------
  'dash.title': 'Overview',
  'dash.loadFailed': 'The overview could not be loaded',
  'dash.clearTitle': 'Nothing outstanding',
  'dash.clear':
    'Every document is posted and every bank line is settled. An open VAT period does not count here — it is normal in the middle of a quarter.',
  'dash.outstandingTitle': 'Something is outstanding',
  'dash.outstanding':
    'The numbers below are counted in the database, not on a page of results, so they are the actual totals.',
  'dash.scope':
    'The overview covers documents, drafts, bank and VAT. Invoices and reports have their own pages — what is left to invoice is not counted here.',

  'dash.unlinked.label': 'Documents not posted',
  'dash.unlinked.explanation':
    'Documents that have been uploaded but not yet linked to an entry. A document that is not posted documents nothing.',
  'dash.unlinked.action': 'Go to documents',
  'dash.undocumented.label': 'Entries without a document',
  'dash.undocumented.explanation':
    'Entries with no document attached. The Danish Bookkeeping Act requires every registration to be documentable, so these are what an auditor asks about.',
  'dash.undocumented.action': 'Go to the journal',
  'dash.drafts.label': 'Draft entries',
  'dash.drafts.explanation':
    'Entries written but not posted yet. They count towards no report, no balance, no VAT return and no SAF-T export — so this is the only place they are remembered.',
  'dash.drafts.action': 'See draft entries',
  'dash.unmatched.label': 'Bank lines without an entry',
  'dash.unmatched.explanation':
    'Lines from the bank statement with nothing posted against them. Each one is a payment in or out that the books do not know about yet.',
  'dash.unmatched.action': 'Go to bank',
  'dash.partial.label': 'Bank lines that do not add up',
  'dash.partial.explanation':
    'Matched, but the entries do not sum to the line’s amount. The difference is still unexplained.',
  'dash.partial.action': 'See the difference',

  'dash.vat.title': 'VAT periods',
  'dash.vat.none': 'No open period. Open one before the return is due.',
  'dash.vat.first': 'First in line',
  'dash.vat.notATask':
    'An open period is not a task in itself. The figures are final only once the period is reported and closed.',
  'dash.vat.action': 'Go to VAT',

  // ---------------------------------------------------------------------------
  // Journal — the ledger
  // ---------------------------------------------------------------------------
  'journal.title': 'Bookkeeping',
  'journal.newEntry': 'New entry',
  'journal.hideForm': 'Hide the form',
  'journal.searchReference': 'Search by document number',
  'journal.empty': 'Nothing has been posted yet.',
  'journal.noMatch': 'No entries with document number "{reference}".',
  'journal.col.account': 'Account',
  'journal.col.text': 'Text',
  'journal.col.debit': 'Debit',
  'journal.col.credit': 'Credit',
  'journal.col.vat': 'VAT',

  // ---------------------------------------------------------------------------
  // SAF-T
  // ---------------------------------------------------------------------------
  'saft.title': 'SAF-T',
  'saft.whatTitle': 'What the file is',
  'saft.what':
    'SAF-T v2.1 is the file the Danish Tax Agency can ask for. It is generated from the books for the chosen period and validated in CI against Erhvervsstyrelsen’s own schema.',
  'saft.choosePeriod': 'Choose a period',
  'saft.bothInclusive': 'Both dates are inclusive.',
  'saft.generate': 'Generate SAF-T file',
  'saft.download': 'Download {filename}',
  'saft.datesRequired': 'Both the from and to dates are required.',
  'saft.generated': 'The SAF-T file has been generated',
  'saft.generatedDetail': 'Download it below.',
  'saft.failed': 'The file was not generated',
  'saft.requiresTitle': 'What the file requires',
  'saft.requires.company':
    'The company needs a CVR number, an address and a bank account — the schema requires all three in the file header, and a placeholder would produce a file that validates and names the wrong sender.',
  'saft.requires.vatCodes':
    'VAT codes may be at most nine characters. Nine of the official codes are longer, and the export is refused rather than shortening a code that has been posted.',
  'saft.requires.contacts':
    'A contact with no address is left out and reported as a warning — it does not block the file.',
  'saft.doneTitle': 'The file has been generated',
  'saft.doneDetail':
    '{filename} · {characters} characters. The file is not stored on the server — it is generated from the books each time it is asked for.',

  // ---------------------------------------------------------------------------
  // Chart of accounts
  // ---------------------------------------------------------------------------
  'accounts.title': 'Chart of accounts',
  'accounts.new': 'New account',
  'accounts.search': 'Search',
  'accounts.searchPlaceholder': 'Number or name',
  'accounts.type': 'Type',
  'accounts.allTypes': 'All',
  'accounts.showInactive': 'Show inactive',
  'accounts.none': 'No accounts match.',
  'accounts.col.number': 'Number',
  'accounts.col.name': 'Name',
  'accounts.col.vat': 'VAT',
  'accounts.col.standard': 'Standard',
  'accounts.inactive': 'inactive',
  'accounts.edit': 'Edit',

  'accounts.type.asset': 'Asset',
  'accounts.type.liability': 'Liability',
  'accounts.type.equity': 'Equity',
  'accounts.type.revenue': 'Revenue',
  'accounts.type.expense': 'Expense',

  'accounts.create.title': 'New account',
  'accounts.create.beforeTitle': 'Before you create one',
  'accounts.create.before':
    'The chart of accounts is Erhvervsstyrelsen’s standard chart, mandatory from January 2026. The reports group by it, so an account you create yourself only counts if its number falls in a range the standard chart already totals.',
  'accounts.create.bankHint':
    'If you need another bank account, 6481–6489 are free and fall under 6490 Likvide beholdninger on their own.',
  'accounts.create.number': 'Account number',
  'accounts.create.namePlaceholder': 'Bank account – Coffee Shop',
  'accounts.create.vatCode': 'VAT code',
  'accounts.create.vatCodeHelp':
    'Optional. Normally set only on revenue and expense accounts.',
  'accounts.create.numberRequired': 'The account number is required.',
  'accounts.create.numberDigits': 'An account number is digits only.',
  'accounts.create.nameRequired': 'The name is required.',
  'accounts.create.created': 'The account has been created',
  'accounts.create.failed': 'The account was not created',

  'accounts.edit.title': 'Account {code}',
  'accounts.edit.vatCodeHelp': 'Empty if the account carries no VAT',
  'accounts.edit.active': 'Active',
  'accounts.edit.activeHelp':
    'An inactive account cannot be chosen when posting, but keeps its history',
  'accounts.edit.saved': 'Saved',
  'accounts.edit.failed': 'Could not save',

  // ---------------------------------------------------------------------------
  // Company settings
  // ---------------------------------------------------------------------------
  'settings.title': 'Settings',
  'settings.loadFailed': 'The company could not be loaded',
  'settings.ownerOnlyTitle': 'Only an owner can change the company',
  'settings.ownerOnly': 'You can see the details but not save them. That requires the owner role.',
  'settings.companySection': 'The company',
  'settings.name': 'Name',
  'settings.cvr': 'CVR number',
  'settings.cvrPlaceholder': '8 digits',
  'settings.cvrHelp': 'Required by the SAF-T export and by an electronic invoice. Clear it by leaving the field empty.',
  'settings.address': 'Address',
  'settings.zip': 'Postcode',
  'settings.city': 'City',
  'settings.fiscalSection': 'Financial year and VAT',
  'settings.fiscalStart': 'The financial year begins',
  'settings.fiscalStartHelp': 'Written MM-DD — a financial year has no year number. 01-01 for a calendar year, 07-01 for a year starting 1 July.',
  'settings.fiscalUnusedTitle': 'The financial year is not used yet',
  'settings.fiscalUnused': 'The reports on /rapporter take a period you choose, and the SAF-T export opens on the calendar year. This field records the company’s real financial year and changes no report.',
  'settings.vatPeriod': 'VAT period',
  'settings.vatPeriodHelp': 'The VAT period says which cycle the company reports on. It does not create periods — those are opened with their own dates under VAT — and it does not move periods that already exist. A closed quarter stays a closed quarter.',
  'settings.vatPeriod.monthly': 'Monthly',
  'settings.vatPeriod.quarterly': 'Quarterly',
  'settings.vatPeriod.biannual': 'Half-yearly',
  'settings.immutableSection': 'Cannot be changed',
  'settings.currency': 'Currency',
  'settings.country': 'Country',
  'settings.currencyHelp': 'Every amount in the books is already in the company’s currency. Changing this field would not convert a single entry — it would change what they all mean. A company kept in another currency needs its own set of books.',
  'settings.saved': 'The settings have been saved',
  'settings.saveFailed': 'The settings were not saved',
  'settings.nameRequired': 'The name is required.',
  'settings.cvrInvalid': 'A CVR number is exactly 8 digits.',
  'settings.fiscalStartInvalid': 'Written MM-DD, for example 01-01. 29 February cannot be used.',

  // ---------------------------------------------------------------------------
  // Invoices — list
  // ---------------------------------------------------------------------------
  'invoices.title': 'Invoices',
  'invoices.newDraft': 'New draft',
  'invoices.filterLabel': 'Filter by status',
  'invoices.filter.all': 'All',
  'invoices.filter.drafts': 'Drafts',
  'invoices.filter.sent': 'Sent',
  'invoices.filter.paid': 'Paid',
  'invoices.none': 'No invoices here yet.',
  'invoices.col.number': 'Number',
  'invoices.col.date': 'Date',
  'invoices.col.customer': 'Customer',
  'invoices.col.status': 'Status',
  'invoices.col.total': 'Total',
  'invoices.noNumberYet': 'No number yet',
  'invoices.open': 'Open',
  'invoices.numbersTitle': 'Numbers are allocated at issue',
  'invoices.numbers': 'A draft has no number. The number is allocated only when the invoice is issued, so a deleted draft cannot leave a hole in a sequence the law requires to be unbroken.',
  'invoices.status.draft': 'Draft',
  'invoices.status.sent': 'Sent',
  'invoices.status.received': 'Received',
  'invoices.status.paid': 'Paid',
  'invoices.status.overdue': 'Overdue',
  'invoices.status.cancelled': 'Cancelled',
  'period.from': 'From the beginning',
  'period.to': 'Until today',
  'period.fromLabel': 'From',
  'period.toLabel': 'To',

  // ---------------------------------------------------------------------------
  // Contacts
  // ---------------------------------------------------------------------------
  'contacts.title': 'Contacts',
  'contacts.new': 'New contact',
  'contacts.showDisabled': 'Show disabled',
  'contacts.none': 'No contacts yet.',
  'contacts.col.name': 'Name',
  'contacts.col.type': 'Type',
  'contacts.col.address': 'Address',
  'contacts.disabled': 'disabled',
  'contacts.type.customer': 'Customer',
  'contacts.type.supplier': 'Supplier',
  'contacts.type.both': 'Both',
  'contacts.create.title': 'New contact',
  'contacts.create.created': 'Contact created',
  'contacts.create.failed': 'The contact was not created',
  'contacts.edit.title': 'Edit {name}',
  'contacts.edit.saved': 'Saved',
  'contacts.edit.failed': 'The contact was not saved',
  'contacts.active': 'Active',
  'contacts.activeHelp': 'A disabled contact cannot be chosen on new invoices but keeps the old ones. Contacts are never deleted — issued invoices refer to them.',
  'contacts.cvr': 'CVR',
  'contacts.cvrPlaceholder': '8 digits',
  'contacts.ean': 'EAN',
  'contacts.eanPlaceholder': '13 digits',
  'contacts.email': 'Email',
  'contacts.address': 'Address',
  'contacts.zip': 'Postcode',
  'contacts.city': 'City',
  'contacts.addressRequiredTitle': 'The address is not optional for e-invoicing',
  'contacts.addressRequired': 'An electronic invoice names both parties in full. Without an address, city and postcode the send is refused, and the contact can only be invoiced on paper.',
  'contacts.nameRequired': 'The contact needs a name.',
  'contacts.cvrInvalid': 'A CVR number is 8 digits.',
  'contacts.eanInvalid': 'An EAN number is 13 digits.',
  'contacts.emailInvalid': 'Enter a valid email address.',

  // ---------------------------------------------------------------------------
  // Reports
  // ---------------------------------------------------------------------------
  'reports.title': 'Reports',
  'reports.choose': 'Choose a report',
  'reports.tab.profitAndLoss': 'Profit and loss',
  'reports.tab.balance': 'Balance sheet',
  'reports.tab.trial': 'Trial balance',
  'reports.asOf': 'As at',
  'reports.revenue': 'Revenue',
  'reports.expenses': 'Expenses',
  'reports.loss': 'Loss',
  'reports.profit': 'Profit',
  'reports.period': 'Period',
  'reports.chartAdvisory': 'The chart is indicative. The amounts in the tables are the exact ones.',
  'reports.vatNotRevenueTitle': 'VAT is not revenue',
  'reports.vatNotRevenue': 'Output VAT is money the company owes SKAT and is not part of revenue. It is on the VAT page.',
  'reports.assets': 'Assets',
  'reports.liabilities': 'Liabilities',
  'reports.equity': 'Equity',
  'reports.resultApartTitle': 'The result for the period stands apart',
  'reports.resultApart': '{amount} has not been moved into equity. That happens at a year-end close, which this system does not yet perform — and leaving the result out entirely would show a difference that is not a difference.',
  'reports.noEntries': 'No entries in the period.',
  'reports.col.account': 'Account',
  'reports.col.name': 'Name',
  'reports.total': 'Total',
  'reports.balance': 'Balance',
  'reports.amount': 'Amount',
  'reports.inBalanceTitle': 'The books balance',
  'reports.inBalance': 'Debit and credit agree. Difference {difference}.',
  'reports.outOfBalanceTitle': 'The books do not balance',
  'reports.outOfBalance': 'Difference {difference}. The database refuses an entry that does not balance, so this is not an ordinary mis-posting — it means the books do not agree with themselves. Investigate before using the figures.',

  // ---------------------------------------------------------------------------
  // VAT
  // ---------------------------------------------------------------------------
  'vat.title': 'VAT',
  'vat.openPeriod': 'Open a period',
  'vat.scopeTitle': 'The figures, not the return',
  'vat.scope': 'This page shows output VAT, input VAT and net VAT for the period. It does not fill in a VAT return and does not allocate the codes to boxes A, B and C — the official code list describes the boxes in free text, and a guess here would mean a wrong declaration to SKAT. The figures are transferred manually.',
  'vat.none': 'No VAT periods yet. Open the first one.',
  'vat.col.period': 'Period',
  'vat.col.status': 'Status',
  'vat.netVat': 'Net VAT',
  'vat.status.closed': 'Filed',
  'vat.status.open': 'Open',
  'vat.notDetermined': 'Not determined',
  'vat.loadFailed': 'The VAT figures could not be loaded',
  'vat.provisionalTitle': 'The period is open — the figures are not final',
  'vat.provisional': 'The amounts are summed from the books every time this page is loaded, and change as entries are posted in the period. Only when the period is closed are they frozen as what was filed.',
  'vat.outgoing': 'Output VAT',
  'vat.incoming': 'Input VAT',
  'vat.netVatHint': 'Output VAT minus input VAT. A positive amount is payable to SKAT.',
  'vat.noFixedRateTitle': 'Codes with no fixed rate',
  'vat.noFixedRate': 'These codes have no fixed rate in the official list, so the posted VAT cannot be checked against one. It rests on whoever posted it.',
  'vat.uncodedTitle': 'Lines with no VAT code',
  'vat.uncoded': {"one": "{count} line in the period has no VAT code. That is usually correct — a bank transfer or a salary payment has none. But a purchase without a code is VAT the company is entitled to and has not deducted, and only a person can tell the difference.", "other": "{count} lines in the period have no VAT code. That is usually correct — a bank transfer or a salary payment has none. But a purchase without a code is VAT the company is entitled to and has not deducted, and only a person can tell the difference."},
  'vat.byCode': 'By code',
  'vat.col.code': 'Code',
  'vat.col.description': 'Description',
  'vat.col.rate': 'Rate',
  'vat.col.base': 'Base',
  'vat.filedTitle': 'The period has been filed',
  'vat.filedOn': 'The figures are frozen as what was filed on {date}. The period cannot be reopened, and nothing can be posted with a date inside it. Corrections belong in the following period — that is how a Danish VAT correction is made anyway.',
  'vat.filed': 'The figures are frozen as what was filed. The period cannot be reopened, and nothing can be posted with a date inside it. Corrections belong in the following period — that is how a Danish VAT correction is made anyway.',
  'vat.ownerOnlyTitle': 'Only an owner can file the period',
  'vat.ownerOnly': 'Filing the VAT is the company’s declaration to SKAT, not a bookkeeping step. You can post entries, open periods and read the figures.',
  'vat.fileAndClose': 'File and close the period',
  'vat.irreversibleTitle': 'This cannot be undone',
  'vat.irreversible': 'When the period is closed, three things happen at once:',
  'vat.irreversible.freeze': 'The figures are frozen on the period as what was filed — net VAT {amount}.',
  'vat.irreversible.noReopen': 'The period can never be reopened.',
  'vat.irreversible.noPosting': 'Nothing can ever again be posted with a date inside the period.',
  'vat.irreversible.enforced': 'The database enforces all three. Corrections belong in the following period.',
  'vat.closeFailed': 'The period was not filed',
  'vat.fileAndCloseShort': 'File and close',
  'vat.closed': 'The period has been filed',
  'vat.closedDetail': 'Net VAT {amount} has been frozen.',
  'vat.opened': 'Period opened',
  'vat.openTitle': 'Open a VAT period',
  'vat.openHelp': 'Both dates are inclusive. Periods must not overlap — an entry belongs to exactly one return. The length is your choice: Danish VAT periods are monthly, quarterly or half-yearly depending on turnover.',
  'vat.startDate': 'Start date',
  'vat.endDate': 'End date',
  'vat.openFailed': 'The period was not opened',
  'vat.open': 'Open',
  'vat.startRequired': 'Choose a start date.',
  'vat.endRequired': 'Choose an end date.',

  // ---------------------------------------------------------------------------
  // Attachments
  // ---------------------------------------------------------------------------
  'att.title': 'Documents',
  'att.filterLabel': 'Filter by state',
  'att.filter.all': 'All',
  'att.none': 'No documents here yet.',
  'att.col.filename': 'Filename',
  'att.col.state': 'State',
  'att.col.size': 'Size',
  'att.col.uploaded': 'Uploaded',
  'att.state.unlinked': 'Not linked',
  'att.state.draftLinked': 'Suggested',
  'att.state.linked': 'Linked',
  'att.uploaded': 'Document uploaded',
  'att.chooseFiles': 'Choose documents',
  'att.dropHere': 'Drag documents here, or click to choose',
  'att.dropHelp': 'PDF, JPEG, PNG, TIFF, HEIC, text, CSV or XML. At most 25 MB.',
  'att.takePhotoLabel': 'Photograph a document',
  'att.takePhoto': 'Take a photo',
  'att.rejectedTitle': 'The file was not uploaded',
  'att.uploadFailed': 'The document could not be uploaded',
  'att.open': 'Open',
  'att.link': 'Link',
  'att.delete': 'Delete',
  'att.openFailed': 'The document could not be opened',
  'att.openFallbackTitle': 'Open the document',
  'att.openFallbackHelp': 'The link works only in this tab.',
  'att.link.title': 'Link {filename}',
  'att.link.irreversibleTitle': 'This cannot be undone',
  'att.link.irreversible': 'Once a document documents an entry it is part of the books and must be kept for five years. It can neither be deleted nor unlinked afterwards. A mistake is corrected with a counter-entry.',
  'att.link.entry': 'Entry',
  'att.link.entryPlaceholder': 'Choose an entry',
  'att.link.entryRequired': 'Choose the entry this document evidences.',
  'att.link.failed': 'The document could not be linked',
  'att.link.linked': 'Document linked',
  'att.delete.title': 'Delete document',
  'att.delete.body': '"{filename}" documents no entry and can be deleted. The file is removed permanently.',
  'att.delete.failed': 'The document was not deleted',
  'att.delete.deleted': 'Document deleted',

  // ---------------------------------------------------------------------------
  // MCP page
  // ---------------------------------------------------------------------------
  'mcp.title': 'MCP',
  'mcp.intro': 'How to connect an AI to your books. The access belongs to you, not to one company.',
  'mcp.tab.general': 'About MCP',
  'mcp.tab.setup': 'Setup',
  'mcp.whatTitle': 'What is MCP?',
  'mcp.what1': 'MCP is a standard that lets an AI program use a system directly instead of asking you to click through it. Connect your AI client and you can write “which entries are missing documents in September?” or “post this receipt for 1,250 kr.”, and it does so through the same rules as the screens here.',
  'mcp.what2': 'It talks to the API — not to the database. Everything that applies to you applies to it too: your role in the company, VAT periods that are closed, entries that cannot be deleted, and the audit trail.',
  'mcp.asymmetryTitle': 'Reading and writing are not the same',
  'mcp.asymmetry1a': 'The AI may ',
  'mcp.asymmetry1b': 'read across your companies',
  'mcp.asymmetry1c': '. But ',
  'mcp.asymmetry1d': 'everything it changes names the company explicitly',
  'mcp.asymmetry1e': ' — every time, in the call itself — and the answer says which company it acted in.',
  'mcp.asymmetry2': 'The reason is that a conversation with an AI gets long, and what it remembered at the start drops out along the way. A person can see the company name at the top of the screen the whole time; it cannot. A read against the wrong books is a wasted answer. A wrong posting can be corrected only by a counter-entry, which the audit trail keeps beside it — for five years.',
  'mcp.asymmetry3': 'The audit trail records which user, which tool, which access key and which company was behind every change.',
  'mcp.canTitle': 'What it can do',
  'mcp.col.area': 'Area',
  'mcp.col.reads': 'Reads',
  'mcp.col.writes': 'Changes',
  'mcp.cannotTitle': 'What it cannot do',
  'mcp.cannot.delete': 'Delete or amend an entry. Nobody can — not even you.',
  'mcp.cannot.otherCompany': 'Reach a company you are not a member of. The key is yours, and it reaches exactly as far as you do.',
  'mcp.cannot.role': 'More than your role allows. If you are an auditor in a company, your AI can read there and not post.',
  'mcp.cannot.silent': 'Change anything without saying where. The company is in the call and in the answer.',
  'mcp.group.bookkeeping': 'Bookkeeping',
  'mcp.group.bookkeeping.reads': 'journal, entries, chart of accounts, balances',
  'mcp.group.bookkeeping.writes': 'create entry, create dimension',
  'mcp.group.attachments': 'Documents',
  'mcp.group.attachments.reads': 'list, search, missing documents, read content',
  'mcp.group.attachments.writes': 'upload, link to entry, store OCR result',
  'mcp.group.bank': 'Bank',
  'mcp.group.bank.reads': 'accounts, transactions, reconciliation',
  'mcp.group.bank.writes': 'reconcile transaction',
  'mcp.group.invoices': 'Invoices',
  'mcp.group.invoices.reads': 'list, details, number sequence, dispatches',
  'mcp.group.invoices.writes': 'create draft, issue, send e-invoice',
  'mcp.group.vat': 'VAT',
  'mcp.group.vat.reads': 'periods, VAT return',
  'mcp.group.vat.writes': 'close period',
  'mcp.group.reports': 'Reports',
  'mcp.group.reports.reads': 'balance sheet, profit and loss, trial balance, SAF-T',
  'mcp.group.reports.writes': '—',
  'mcp.group.inbound': 'Incoming mail',
  'mcp.group.inbound.reads': 'received mail, senders',
  'mcp.group.inbound.writes': 'approve sender',
  'mcp.group.company': 'Company',
  'mcp.group.company.reads': 'registration details, overview',
  'mcp.group.company.writes': 'edit registration details',
  'mcp.setup.browser.title': 'Connect by signing in',
  'mcp.setup.browser.badge': 'Recommended',
  'mcp.setup.browser.body': 'Paste the address into your AI client and sign in. The client sends you here, you pick a company, and it gets its own key. There is nothing to copy.',
  'mcp.setup.browser.step1': 'Copy the address below.',
  'mcp.setup.browser.step2': 'In Claude: Settings → Connectors → Add custom connector. Paste the address. There is no field for a key, and that is the point.',
  'mcp.setup.browser.step3': 'Sign in, choose which company the AI reads in by default, and press Allow.',
  'mcp.setup.browser.noKey': 'Afterwards the grant is listed on Min konto under the client’s name, and that is where you revoke it.',
  'mcp.setup.file.title': 'Or paste a key into a file',
  'mcp.setup.file.body': 'Claude Code and most editors read a configuration file and can send a key. The Claude desktop and web apps cannot: there, signing in is the only way.',
  'mcp.setup.file.company': 'A key from Min konto names no company. If you belong to more than one, add “X-Company-Id” beside Authorization — otherwise the server does not know which books to read.',
  'consent.title': '{client} is asking for access to your books',
  'consent.intro': 'Allow it and the application gets its own key. The key is yours: it reaches exactly as far as you do, and no further.',
  'consent.can.read': 'It can read across the companies you belong to.',
  'consent.can.write': 'It can change something only by naming the company in the call itself — every time — and the answer says which company it acted in.',
    'consent.scopesLabel': 'What you are granting',
  'consent.scopesHelp':
    'Clear anything you would rather not give. You cannot grant more than was asked for.',
'consent.can.role': 'It can do no more than your role allows. If you are an auditor in a company, it can read there and not post.',
  'consent.can.audit': 'Everything it changes is in the audit trail, with you, the tool and the key.',
  'consent.companyLabel': 'Company its reads happen in',
  'consent.companyHelp': 'Reads only. To post anything it names the company in the call itself.',
  'consent.returnsTo': 'You will be sent back to {host}.',
  'consent.allow': 'Allow',
  'consent.deny': 'Refuse',
  'consent.failedTitle': 'Access was not granted',
  'consent.goneTitle': 'This request no longer exists',
  'consent.goneBody': 'It has expired or has already been used. Start again in the application that sent you here.',
  'consent.missingTitle': 'There is nothing to approve',
  'consent.missingBody': 'This page was opened without a request. Start in the application you want to grant access to.',
  'consent.revokeHint': 'You can revoke this access at any time on Min konto.',
  'mcp.setup.step1a': 'The key is created on ',
  'mcp.setup.step1b': ', not here. It belongs to you and not to a company, so it lives with the rest of your own details.',
  'mcp.setup.onceOnly': 'The key is shown once. Copy it straight away — it is stored only as a hash and cannot be shown again.',
  'mcp.setup.goToAccount': 'Go to My account',
  'mcp.setup.overHttp': 'The server runs over HTTP, so the client does not have to start a program on your machine. It only needs to reach the address.',
  'mcp.setup.step3a': 'Most clients — Claude Desktop, Claude Code, Cursor — read a file in this format. Replace ',
  'mcp.setup.step3b': ' with the key from My account.',
  'mcp.setup.copyConfig': 'Copy the configuration',
  'mcp.setup.step4': '4. Try it',
  'mcp.setup.try': 'Ask your AI something it can only know from here — “how many entries are missing documents?” is a good first test. The answer begins with which company it looked in.',
  'mcp.setup.noExpiryTitle': 'The key does not expire on its own',
  'mcp.setup.noExpiry': 'It keeps working until you revoke it on My account. If you lose the machine it sits on, that is where you close it — and only it. Your other keys and your password are untouched.',

  // ---------------------------------------------------------------------------
  // My account
  // ---------------------------------------------------------------------------
  'account.title': 'My account',
  'account.intro': 'Your own details. They are the same whichever company you are looking at.',
  'account.you': 'You',
  'account.loadFailed': 'The profile could not be loaded',
  'account.name': 'Name',
  'account.nameHelp': 'Shown in the audit trail beside what you changed.',
  'account.email': 'Email',
  'account.emailHelp': 'Used to sign in. Cannot be changed here yet.',
  'account.lastLogin': 'Last signed in {when}.',
  'account.nameSaved': 'The name has been saved.',
  'account.nameFailed': 'The name was not saved',
  'account.password': 'Password',
  'account.currentPassword': 'Current password',
  'account.newPassword': 'New password',
  'account.repeatPassword': 'Repeat the new password',
  'account.passwordHelp': 'When the password changes you are signed out everywhere except here. That is deliberate: a password is changed because someone else might have the old one.',
  'account.changePassword': 'Change password',
  'account.passwordChanged': 'The password has been changed',
  'account.passwordChangedDetail': 'You have been signed out everywhere else.',
  'account.passwordFailed': 'The password was not changed',
  'account.currentPasswordRequired': 'Enter your current password.',
  'account.newPasswordTooShort': 'At least 8 characters.',
  'account.passwordsDiffer': 'The two passwords are not the same.',
  'account.twoFactor': 'Two-factor',
  'account.twoFactorOn': 'On',
  'account.twoFactorOff': 'Off',
  'account.twoFactorHelp': 'A code from an app on your phone, in addition to the password. The books cannot be deleted or amended — an entry can only be reversed — so whoever can sign in can leave permanent marks.',
  'account.turnOn': 'Turn on',
  'account.turnOff': 'Turn off',
  'account.turnOnTitle': 'Turn on two-factor',
  'account.turnOffTitle': 'Turn off two-factor',
  'account.turnOffPrompt': 'Enter your password to confirm. Afterwards you can sign in with the password alone.',
  'account.turnOnPrompt': 'Enter your password. You will then get a QR code to scan with your authenticator app.',
  'account.continue': 'Continue',
  'account.scanPrompt': 'Scan the code with your authenticator app, then enter the six-digit number it shows.',
  'account.qrAlt': 'QR code for an authenticator app',
  'account.cannotScan': 'Cannot scan?',
  'account.backupCodes': 'Backup codes',
  'account.backupCodesHelp': 'Keep them somewhere safe. They are the only way in if you lose the phone — they are shown only now.',
  'account.copyBackupCodes': 'Copy the backup codes',
  'account.codeFromApp': 'Code from the app',
  'account.notOnYet': 'Two-factor is not on until the code has been accepted.',
  'account.twoFactorEnabled': 'Two-factor is on',
  'account.twoFactorEnabledDetail': 'Keep your backup codes somewhere safe.',
  'account.twoFactorDisabled': 'Two-factor is off.',
  'account.tokens': 'Access keys for AI',
  'account.tokensHelp': 'A key your AI uses to reach your books through MCP. It belongs to you, not to one company — what it may do in a company is decided by your role there, every time it is used. Make one per machine, so you can revoke exactly the one you lose.',
  'account.tokenOnceTitle': 'The key is shown only now',
  'account.tokenOnce': 'Copy it into your AI client straight away. It is stored only as a hash, so it cannot be shown again — lose it and you make a new one.',
  'account.copyToken': 'Copy the key',
    'account.tokenScopes': 'What this key may do',
  'account.tokenScopesHelp':
    'The bottom three cannot be undone, which is why they have to be chosen. Everything else can be corrected afterwards with a counter-entry.',
  'scope.mcp:read': 'Read the books',
  'scope.mcp:write': 'Post, reconcile and invoice',
  'scope.accounts:write': 'Change the chart of accounts',
  'scope.vat:close': 'Close a VAT period',
  'scope.einvoice:send': 'Send e-invoices',
  'scope.mcp:readHelp': 'Can see everything, change nothing.',
  'scope.mcp:writeHelp': 'Can be corrected afterwards with a counter-entry.',
  'scope.accounts:writeHelp':
    'Cannot be undone: an account cannot be deleted, and its number and type cannot be changed afterwards.',
  'scope.vat:closeHelp': 'Cannot be reopened. Corrections belong in the following period.',
  'scope.einvoice:sendHelp': 'Sends a document to a real recipient under your CVR.',
'account.tokenName': 'Name for the key',
  'account.tokenNameHelp': 'So you can tell it from the others.',
  'account.createToken': 'Create key',
  'account.tokenNameRequired': 'Give the key a name.',
  'account.createTokenFailed': 'The key was not created',
  'account.revokeFailed': 'The key was not revoked',
  'account.tokensFailed': 'The keys could not be loaded',
  'account.noTokens': 'You have no keys yet.',
  'account.col.status': 'Status',
  'account.col.lastUsed': 'Last used',
  'account.revoke': 'Revoke',
  'account.token.revoked': 'revoked',
  'account.token.expired': 'expired',
  'account.token.active': 'active',
  'account.revokedStay': 'Revoked keys stay on the list. The audit trail records which key made every change, and you have to be able to look it up afterwards.',
  'account.language': 'Language',
  'account.languageSelect': 'Choose language',
  'account.languageHelp': 'The language follows you, not the company — it is stored on your user, so another browser opens in the same one.',
  'account.languageScopeTitle': 'The books are Danish in both languages',
  'account.languageScope': 'The language covers the screens: menus, buttons, help text and errors. The chart of accounts, the VAT codes, SAF-T and OIOUBL stay in Danish — they are Erhvervsstyrelsen’s and SKAT’s own words, and an auditor asking about 1010 Salg af varer og ydelser means that exact text.',
  'account.languageSaved': 'The language has been saved.',
  'account.languageFailed': 'The language was not saved',
  'account.language.da': 'Dansk',
  'account.language.en': 'English',

  // ---------------------------------------------------------------------------
  // Entry and invoice forms
  // ---------------------------------------------------------------------------
  'entryForm.posted': 'Posted',
  'entryForm.date': 'Date',
  'entryForm.description': 'Entry text',
  'entryForm.reference': 'Doc. no.',
  'entryForm.accountPlaceholder': 'Account number',
  'entryForm.accountLine': 'Account, line {n}',
  'entryForm.textLine': 'Text, line {n}',
  'entryForm.debitLine': 'Debit, line {n}',
  'entryForm.creditLine': 'Credit, line {n}',
  'entryForm.removeLine': 'Remove line {n}',
  'entryForm.invalidAmount': 'Invalid amount',
  'entryForm.inBalance': 'In balance',
  'entryForm.difference': 'Difference {amount}',
  'entryForm.bothSides': 'A line can have either debit or credit — not both.',
  'entryForm.failed': 'The entry could not be posted',
  'entryForm.post': 'Post',
  'entryForm.irreversible': 'A posting cannot be amended or deleted afterwards. Mistakes are corrected with a counter-entry.',
  'draft.created': 'Draft created',
  'draft.title': 'New invoice draft',
  'draft.customer': 'Customer',
  'draft.customerPlaceholder': 'Choose a customer',
  'draft.date': 'Invoice date',
  'draft.dueDate': 'Due date',
  'draft.col.description': 'Description',
  'draft.col.quantity': 'Quantity',
  'draft.col.unitPrice': 'Unit price',
  'draft.col.vatCode': 'VAT code',
  'draft.accountPlaceholder': 'Revenue',
  'draft.removeLine': 'Remove line',
  'draft.addLine': 'Add line',
  'draft.subtotal': 'Subtotal',
  'draft.vatNote': 'VAT is calculated from the VAT code’s rate when the invoice is issued. A code with no fixed rate is refused rather than treated as 0 %.',
  'draft.failed': 'The draft was not created',
  'draft.save': 'Save draft',

  // ---------------------------------------------------------------------------
  // Inbound email
  // ---------------------------------------------------------------------------
  'inbound.title': 'Incoming mail',
  'inbound.whatTitle': 'What this page does',
  'inbound.what': 'Forward an invoice to the company’s address and it lands in the document list. It posts nothing by itself: a received document is not attached to any entry until a person attaches it.',
  'inbound.address': 'The address',
  'inbound.addressFailed': 'The address could not be loaded',
  'inbound.noAddress': 'There is no address yet.',
  'inbound.inactive': 'Inactive',
  'inbound.noDomainTitle': 'Mail cannot be received yet',
  'inbound.noDomainA': 'The first part of the address is set to ',
  'inbound.noDomainB': ', but no receiving domain has been configured on the server. No mail can arrive until that is in place.',
  'inbound.newAddress': 'New address',
  'inbound.newAddressHelp': 'The part before the @. It must be unique across the whole installation — it is a real mailbox.',
  'inbound.saveAddress': 'Save address',
  'inbound.ownerOnly': 'Only an owner can change the address.',
  'inbound.addressSet': 'The address has been set',
  'inbound.addressNotSet': 'The address was not set',
  'inbound.localPartInvalid': 'Lower-case letters, digits, dot, hyphen and underscore. Must begin and end with a letter or digit.',
  'inbound.senders': 'Approved senders',
  'inbound.sendersHelp': 'Mail from other addresses is refused — and recorded, so it does not vanish silently.',
  'inbound.sendersFailed': 'The list could not be loaded',
  'inbound.noSendersTitle': 'No senders are approved',
  'inbound.noSenders': 'Everything sent to the address is refused until at least one sender is approved.',
  'inbound.col.email': 'Email',
  'inbound.col.note': 'Note',
  'inbound.actions': 'Actions',
  'inbound.removeSender': 'Remove {email}',
  'inbound.approveSender': 'Approve a sender',
  'inbound.senderPlaceholder': 'invoice@supplier.dk',
  'inbound.notePlaceholder': 'The supplier’s name',
  'inbound.approve': 'Approve',
  'inbound.approved': 'Sender approved',
  'inbound.approvedDetail': '{email}. Ask the sender to send the document again.',
  'inbound.approveFailed': 'The sender was not approved',
  'inbound.revoked': 'The approval has been removed',
  'inbound.revokeFailed': 'The approval was not removed',
  'inbound.emailInvalid': 'Enter an email address.',
  'inbound.log': 'Received mail',
  'inbound.filter.all': 'All',
  'inbound.filter.accepted': 'Received',
  'inbound.filter.rejected': 'Refused',
  'inbound.logFailed': 'The mail could not be loaded',
  'inbound.logEmpty': 'Nothing has been received.',
  'inbound.col.received': 'Received',
  'inbound.col.from': 'Sender',
  'inbound.col.subject': 'Subject',
  'inbound.col.files': 'Files',
  'inbound.col.outcome': 'Outcome',
  'inbound.approveThis': 'Approve {email}',
  'inbound.logNote': 'Everything that arrives is recorded — including what is refused. An invoice from an unapproved sender would otherwise vanish without trace, and be missing from the books with nothing anywhere saying it had been sent.',
  'inbound.outcome.accepted': 'Received',
  'inbound.outcome.senderNotAllowed': 'Sender not approved',
  'inbound.outcome.duplicate': 'Duplicate',
  'inbound.outcome.unknownAddress': 'Unknown address',
  'inbound.outcome.partial': 'Partly received',
  'inbound.outcome.noAttachments': 'No attached files',

  // ---------------------------------------------------------------------------
  // Inbound email
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Bank
  // ---------------------------------------------------------------------------
  'bank.title': 'Bank',
  'bank.newAccount': 'New bank account',
  'bank.noAccountsTitle': 'No bank accounts yet',
  'bank.noAccountsA': 'A bank account is tied to exactly one account in the chart of accounts. The standard chart has 6480 Bankkonto.',
  'bank.noAccountsB1': 'If you need more, the numbers 6481–6489 are free — but the account must be ',
  'bank.noAccountsB2': 'created in the chart of accounts first',
  'bank.noAccountsB3': '. It then falls under 6490 Likvide beholdninger on its own.',
  'bank.account': 'Bank account',
  'bank.filterLabel': 'Filter by reconciliation',
  'bank.filter.all': 'All',
  'bank.filter.partial': 'Partly',
  'bank.noTransactions': 'No lines on the statement yet.',
  'bank.col.date': 'Date',
  'bank.col.text': 'Text',
  'bank.col.amount': 'Amount',
  'bank.col.balance': 'Balance',
  'bank.col.reconciliation': 'Reconciliation',
  'bank.reconcile': 'Reconcile',
  'bank.status.unmatched': 'Not reconciled',
  'bank.status.partial': 'Partly reconciled',
  'bank.status.matched': 'Reconciled',
  'bank.summaryFailed': 'The reconciliation could not be loaded',
  'bank.unmatched': 'Not reconciled',
  'bank.transactionCount': 'Number of lines',
  'bank.lastStatement': 'Last statement',
  'bank.import': 'Import a statement',
  'bank.importHelp': 'Send the file exactly as the bank exported it. Open it in Excel and save, and the character set or separator changes — and the file is no longer evidence of what the bank wrote.',
  'bank.file': 'File',
  'bank.filePlaceholder': 'Choose a CSV file',
  'bank.fileRequired': 'Choose the statement file.',
  'bank.reader': 'Reader',
  'bank.readerHelp': 'Chosen automatically from the file',
  'bank.readerPlaceholder': 'Automatic',
  'bank.importButton': 'Import',
  'bank.alreadyImported': 'Already imported',
  'bank.imported': 'Statement imported',
  'bank.alreadyImportedDetail': 'The file is identical to an earlier import. Nothing was added.',
  'bank.importedDetail': '{count} lines added.',
  'bank.importFailed': 'The statement was not imported',
  'bank.readTitle': 'Statement read',
  'bank.readSummary': '{reader} · {from}–{to} · {rows} rows read, {imported} added, {duplicates} already posted.',
  'bank.gapTitle': 'A statement is missing in between',
  'bank.gap': 'The previous statement ended at {previous}, and this one begins at {opening}. The import went through — gaps are legal — but a statement is missing.',
  'bank.skippedTitle': 'Rows skipped',
  'bank.accountCreated': 'Bank account created',
  'bank.newAccountTitle': 'New bank account',
  'bank.accountName': 'Name',
  'bank.accountNamePlaceholder': 'Nordea Erhverv',
  'bank.ledgerAccount': 'Account in the chart of accounts',
  'bank.ledgerMode': 'How the ledger account is chosen',
  'bank.ledgerMode.existing': 'Pick an account',
  'bank.ledgerMode.new': 'Create a new account',
  'bank.ledgerAccountPlaceholder': 'Search by number or name',
  'bank.ledgerAccountHelp':
    'Cannot be changed afterwards: lines are reconciled against it. Only asset accounts no other bank account already uses.',
  'bank.noEligibleAccounts': 'No free asset accounts. Create a new one instead.',
  'bank.newAccountCode': 'Account number',
  'bank.newAccountCodeHelp':
    '6481–6489 are free in the standard chart and roll up into 6490 Likvide beholdninger on their own. The account is created as an asset account together with the bank account.',
  'bank.newAccountName': 'Account name',
  'bank.newAccountNamePlaceholder': 'Bank account – Coffee Shop',
  'bank.newAccountCodeRequired': 'The account number is required.',
  'bank.newAccountCodeDigits': 'An account number is digits only.',
  'bank.newAccountNameRequired': 'The account needs a name.',
  'bank.iban': 'IBAN',
  'bank.ibanPlaceholder': 'Optional',
  'bank.accountFailed': 'The bank account was not created',
  'bank.nameRequired': 'The bank account needs a name.',
  'bank.ledgerAccountRequired': 'Choose the account in the chart of accounts.',

  // ---------------------------------------------------------------------------
  // Bank matching
  // ---------------------------------------------------------------------------
  'match.title': 'Reconcile {date} · {amount}',
  'match.noBankText': 'No text from the bank',
  'match.postedSoFar': 'Posted so far',
  'match.difference': 'Difference',
  'match.postedEntries': 'Posted entries',
  'match.removeMatch': 'Remove match',
  'match.removeNote': 'This removes only the link. The entry stays — entries are immutable, so a wrong entry is corrected with a counter-entry.',
  'match.removeFailed': 'The match was not removed',
  'match.reconcile': 'Reconcile',
  'match.modeLabel': 'How should the line be reconciled',
  'match.mode.existing': 'Already posted',
  'match.mode.fresh': 'Post now',
  'match.close': 'Close',
  'match.existingHelp': 'A customer’s payment of an invoice, or a supplier invoice posted before payment. The entry exists — it only needs linking to the bank line.',
  'match.entry': 'Entry',
  'match.entryPlaceholder': 'Choose an entry',
  'match.entryRequired': 'Choose the entry this bank line belongs to.',
  'match.reconcileFailed': 'Could not reconcile',
  'match.link': 'Link',
  'match.reconciled': 'Reconciled',
  'match.fullyReconciled': 'The bank line is fully reconciled.',
  'match.stillMissing': '{amount} is still outstanding.',
  'match.freshWarningTitle': 'Post only if the event is not already posted',
  'match.freshWarning': 'The bank account takes one side automatically. If the amount is a customer’s payment of an invoice that is already posted, use “Already posted” instead — otherwise the sale is recognised twice, and VAT is paid on it twice.',
  'match.contraAccount': 'Contra account',
  'match.contraAccountPlaceholder': 'Choose an account',
  'match.contraAccountRequired': 'Choose the contra account.',
  'match.text': 'Text',
  'match.textRequired': 'Write a text for the entry.',
  'match.entryDate': 'Entry date',
  'match.entryDateRequired': 'Choose the entry date.',
  'match.postFailed': 'Could not post',
  'match.postAndReconcile': 'Post and reconcile',
  'match.posted': 'Posted and reconciled',

  // ---------------------------------------------------------------------------
  // Invoice detail
  // ---------------------------------------------------------------------------
  'invDetail.draftTitle': 'Invoice draft',
  'invDetail.title': 'Invoice {number}',
  'invDetail.noCustomer': 'No customer chosen',
  'invDetail.dates': '{date} · due {dueDate}',
  'invDetail.total': 'Total',
  'invDetail.subtotal': 'Subtotal {amount}',
  'invDetail.vat': 'VAT {amount}',
  'invDetail.close': 'Close',
  'invDetail.draft': 'Draft',
  'invDetail.deleteDraft': 'Delete draft',
  'invDetail.draftDeleted': 'Draft deleted',
  'invDetail.draftDeletedDetail': 'No number sequence was affected.',
  'invDetail.deleteFailed': 'The draft was not deleted',
  'invDetail.issue': 'Issue invoice',
  'invDetail.irreversibleTitle': 'This cannot be undone',
  'invDetail.irreversible1': 'Issuing allocates the next invoice number and posts the invoice to the books — both in one transaction.',
  'invDetail.irreversible2': 'After that the invoice is locked. It can be neither amended nor deleted, because the number must stay in the sequence. A mistake is corrected with a credit note.',
  'invDetail.missingAccountTitle': 'A line is missing an account',
  'invDetail.missingAccount': 'Every line needs a revenue account before the invoice can be issued.',
  'invDetail.issueFailed': 'The invoice was not issued',
  'invDetail.undo': 'Cancel',
  'invDetail.issueAndPost': 'Issue and post',
  'invDetail.issued': 'Invoice {number} issued',
  'invDetail.issuedDetail': 'The number has been allocated and the invoice posted.',
  'invDetail.issuedSection': 'Issued',
  'invDetail.lockedTitle': 'The invoice is locked',
  'invDetail.locked': 'An issued invoice can be neither amended nor deleted — the number must stay in the sequence. A mistake is corrected with a credit note, which draws from the same sequence.',
  'invDetail.markPaid': 'Mark as paid',
  'invDetail.markedPaid': 'Marked as paid',
  'invDetail.eInvoice': 'Electronic invoice',
  'invDetail.format': 'Format',
  'invDetail.format.oioubl': 'OIOUBL 2.1 (Danish)',
  'invDetail.format.peppol': 'Peppol BIS 3.0 (international)',
  'invDetail.showDocument': 'Show document',
  'invDetail.documentBuilt': 'Document built',
  'invDetail.documentBuiltDetail': 'Nothing has been sent. Building is read-only.',
  'invDetail.send': 'Send',
  'invDetail.buildNote': 'Building is read-only and repeatable, so the document can be inspected before anything reaches a customer. The documents are not validated against the UBL schemas or Peppol’s Schematron — an error is found first by the recipient’s access point.',
  'invDetail.documentFailed': 'The document could not be built',
  'invDetail.sendFailed': 'The invoice was not sent',
  'invDetail.dispatches': 'Dispatches',
  'invDetail.notDeliveredTitle': 'Not delivered',
  'invDetail.notDelivered1a': 'The invoice was ',
  'invDetail.notDelivered1b': 'not',
  'invDetail.notDelivered1c': ' sent to the customer. No access point is configured, so the send ran against ',
  'invDetail.notDelivered1d': ', which builds and records the document without opening a connection.',
  'invDetail.notDelivered2a': 'The dispatch is recorded as ',
  'invDetail.notDelivered2b': ', but that only says the attempt ran without error — not that anything arrived.',
  'invDetail.deliveredTitle': 'Delivered',
  'invDetail.delivered': '{number} sent as {format} via {provider}.',
  'invDetail.receipt': ' Receipt: {messageId}',

  // ---------------------------------------------------------------------------
  // Stragglers
  // ---------------------------------------------------------------------------
  'contacts.missingAddress': 'No address — cannot be e-invoiced',
  'contacts.edit': 'Edit',
  'mcp.setup.keyPlaceholder': 'YOUR_KEY_HERE',
  'vat.undo': 'Cancel',
  'vat.show': 'Show',

  // ---------------------------------------------------------------------------
  // Audit trail
  // ---------------------------------------------------------------------------
  'audit.title': 'Audit trail',
  'audit.intro': 'Who changed what, when — and with which key. Newest first.',
  'audit.forbiddenTitle': 'Your role cannot read the audit trail',
  'audit.forbidden': 'The audit trail is a record of what your colleagues have done. It can be read by an owner, an auditor and an external accountant — the roles whose job is inspection. You can post entries and read the books as before.',
  'audit.filterAction': 'Action',
  'audit.allActions': 'All actions',
  'audit.loadFailed': 'The audit trail could not be loaded',
  'audit.empty': 'Nothing recorded that matches.',
  'audit.col.when': 'When',
  'audit.col.action': 'Action',
  'audit.col.who': 'Who',
  'audit.col.change': 'Change',
  'audit.showChange': 'Show',
  'audit.hideChange': 'Hide',
  'audit.newer': 'Newer',
  'audit.older': 'Older',
  'audit.immutable': 'The audit trail can be neither amended nor deleted — the database refuses it. It is kept for five years alongside the books.',

  // ---------------------------------------------------------------------------
  // Mileage log
  // ---------------------------------------------------------------------------
  'nav.trips': 'Mileage log',
  'trip.title': 'Mileage log',
  'trip.intro': 'Business driving in your own car. The rate is Skatterådets — it is not chosen here, it is looked up from the date of the trip.',
  'trip.legalNote': 'The kørebog is required by ligningsloven §9B. SKAT can ask for it during an inspection, and it must show the date, start, end, distance and purpose of every trip.',
  'trip.log': 'Log trip',
  'trip.logTitle': 'Log a trip',
  'trip.date': 'Date',
  'trip.from': 'Start address',
  'trip.to': 'End address',
  'trip.km': 'Kilometres',
  'trip.purpose': 'Purpose',
  'trip.purposeHelp': 'For example "Customer meeting at Sipotek A/S". SKAT asks for the purpose by name.',
  'trip.isPrivate': 'Private driving',
  'trip.isPrivateHelp': 'Private driving is recorded in the log as well, but is worth nothing and cannot be posted.',
  'trip.useGps': 'Use my location',
  'trip.gpsSearching': 'Finding your location …',
  'trip.gpsDenied': 'Location was refused. Type the address instead.',
  'trip.gpsUnavailable': 'Your location could not be found. Type the address instead.',
  'trip.save': 'Save trip',
  'trip.saved': 'Trip logged',
  'trip.saveFailed': 'The trip could not be saved',
  'trip.queued': 'No connection — the trip is saved on this device and will be sent when you are back online.',
  'trip.post': 'Post',
  'trip.posted': 'Trip posted',
  'trip.postFailed': 'The trip could not be posted',
  'trip.postConfirmTitle': 'Post this trip?',
  'trip.postConfirm': 'This creates a journal entry of {amount}. The entry cannot be edited afterwards — a mistake is corrected with a counter-entry.',
  'trip.delete': 'Delete',
  'trip.deleted': 'Trip deleted',
  'trip.deleteFailed': 'The trip could not be deleted',
  'trip.deleteConfirmTitle': 'Delete this trip?',
  'trip.deleteConfirm': 'The trip has not been posted and will be removed from the log entirely.',
  'trip.empty': 'No trips yet.',
  'trip.loadFailed': 'The mileage log could not be loaded',
  'trip.col.date': 'Date',
  'trip.col.route': 'Trip',
  'trip.col.km': 'Km',
  'trip.col.rate': 'Rate',
  'trip.col.amount': 'Allowance',
  'trip.col.status': 'Status',
  'trip.statusPosted': 'Posted',
  'trip.statusDraft': 'Not posted',
  'trip.private': 'Private',
  'trip.year': 'Year',
  'trip.ytd': 'Business kilometres this year',
  'trip.ytdOfThreshold': '{km} of {threshold} km at the higher rate',
  'trip.thresholdPassed': 'The {threshold} km threshold has been passed. Kilometres above it are paid at the lower rate.',
  'trip.totalDeduction': 'Allowance in total',
  'trip.unposted': 'Not posted',
  'trip.unpostedCount': { one: '1 trip is waiting to be posted', other: '{count} trips are waiting to be posted' },
  'trip.rates': 'Rates',
  'trip.rateTier1': 'Up to {threshold} km',
  'trip.rateTier2': 'Above {threshold} km',
  'trip.rateSource': 'Source',
  'trip.export': 'Download for SKAT',
  'trip.exportFailed': 'The mileage log could not be downloaded',
  'trip.exportPdfMissing': 'The PDF export has not been built yet. The CSV file carries the same information.',
  'trip.splitAcrossTiers': '{tier1} km at the higher rate, {tier2} km at the lower',

  // ---------------------------------------------------------------------------
  // Camera
  // ---------------------------------------------------------------------------
  'camera.title': 'Photograph a document',
  'camera.starting': 'Opening the camera …',
  'camera.viewfinder': 'Camera viewfinder',
  'camera.take': 'Take photo',
  'camera.retake': 'Retake',
  'camera.use': 'Use this photo',
  'camera.previewAlt': 'The photo you just took',
  'camera.reviewHelp': 'Can you read the amount, the date and the supplier? If not, take it again — this document is kept for five years.',
  'camera.fallbackTitle': 'Using the device camera',
  'camera.unsupported': 'This browser cannot show a viewfinder. The device camera will be used instead.',
  'camera.denied': 'Camera access was refused. You can still use the device camera.',
  'camera.unavailable': 'The camera could not be opened. The device camera will be used instead.',
  'camera.openSystemCamera': 'Open camera',

  // ---------------------------------------------------------------------------
  // Offline
  // ---------------------------------------------------------------------------
  'offline.queued': { one: '1 item is waiting to be sent', other: '{count} items are waiting to be sent' },
  'offline.queuedHelp': 'What you captured or logged without a connection is held on this device and sent automatically once you are back online.',
  'offline.sendNow': 'Send now',
  'offline.sending': 'Sending …',
  'offline.sent': { one: '1 item sent', other: '{count} items sent' },
  'offline.blocked': 'Cannot send yet: {reason}',
  'offline.stuck': 'The server refused it: {reason}',
  'offline.retry': 'Try again',
  'offline.discard': 'Discard',
  'offline.discardConfirm': 'Discard for good',
  'offline.discardHelp': 'It exists only on this device and cannot be recovered.',
  'offline.otherCompany': 'another company',
  'offline.itemAttachment': 'Document {name}',
  'offline.itemTrip': 'Trip {date}: {from} → {to}',
  'offline.updateAvailable': 'A new version is available',
  'offline.updateHelp': 'Reload to start using it.',
  'offline.update': 'Reload',

  // ---------------------------------------------------------------------------
  // Mobile app
  // ---------------------------------------------------------------------------
  'mobile.title': 'Mobile app',
  'mobile.intro': 'What gets done with a phone in hand: photograph a receipt and log a trip. Installed on a phone, the app is only those two actions — no menu and no bookkeeping, just the company they are saved to.',
  'mobile.installTitle': 'Install on your phone',
  'mobile.installHelp': 'The app opens straight onto the two buttons and also works without a connection.',
  'mobile.install': 'Install app',
  'mobile.installed': 'The app is installed on this device.',
  'mobile.installManual': 'Open {url} in Chrome on the phone, tap ⋮ in the top right and choose "Install app".',
  'mobile.open': 'Open the app view',
  'mobile.actionsTitle': 'Actions',
  'mobile.companyHint': 'Receipts and trips are saved to the selected company.',

  // ---------------------------------------------------------------------------
  // Notifications
  // ---------------------------------------------------------------------------
  'push.title': 'Notifications',
  'push.intro': 'Be told on your phone when a document has been sitting unfiled for more than a week, or when a trip in the mileage log has not been posted.',
  'push.enable': 'Turn notifications on',
  'push.disable': 'Turn off on this device',
  'push.enabled': 'Notifications are on for this device',
  'push.unsupported': 'This browser does not support notifications.',
  'push.notConfigured': 'Notifications are not configured on this server.',
  'push.denied': 'You have blocked notifications for this site. They can be re-enabled in the browser settings.',
  'push.failed': 'Notifications could not be turned on',
  'push.devices': 'Registered devices',
  'push.deviceSince': 'registered {date}',
  'push.deviceExpired': 'expired',
  'push.noDevices': 'No devices are registered.',
};
